import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertCircle, RefreshCw } from "lucide-react";

// ═══════════════════════════════════════════════════════════════════════
// ErrorBoundary — catch render errors so one broken surface doesn't blank
// the whole Agentic Operating System. Two intended mount points:
//   1. Top-level in App.tsx as the catastrophic catch-all
//   2. Per-screen inside SchemaRenderer.tsx so a broken screen leaves
//      navigation usable
//
// Reports to console + window.Sentry if loaded. Server-side Sentry
// already initialized (server/instrument.ts); client-side initialization
// is a separate batch (J1-CLIENT). Until then, errors land in browser
// console — visible to the user via DevTools, visible to us via
// Coolify container logs if they reproduce server-side.
// ═══════════════════════════════════════════════════════════════════════

interface ErrorBoundaryProps {
  children: ReactNode;
  // Friendly name for the boundary's scope; shown in the fallback UI.
  // e.g. "Operator", "Deals screen", "the Agentic Operating System".
  scope?: string;
  // Optional custom fallback. If omitted, the default fallback renders.
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Always log to console — DevTools is the user's debug path until
    // J1-CLIENT lands.
    // eslint-disable-next-line no-console
    console.error(`[ErrorBoundary:${this.props.scope ?? "unknown"}]`, error, info);
    // Report to Sentry if it's loaded on window. Server-side Sentry
    // already exists; client-side isn't yet, but if it lands later this
    // boundary will start reporting automatically.
    const w = window as any;
    if (w?.Sentry?.captureException) {
      try {
        w.Sentry.captureException(error, {
          tags: { boundary_scope: this.props.scope ?? "unknown" },
          extra: { componentStack: info.componentStack },
        });
      } catch {
        /* swallow — never let reporting fail the fallback render */
      }
    }
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    if (this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.reset);
      }
      const scope = this.props.scope ?? "this part of your Agentic Operating System";
      return (
        <div className="flex flex-col items-center justify-center p-10 text-center min-h-[300px]">
          <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mb-3 text-red-500">
            <AlertCircle className="w-6 h-6" />
          </div>
          <p className="text-sm font-medium text-slate-900 mb-1">
            Something broke in {scope}.
          </p>
          <p className="text-xs text-slate-500 max-w-sm mb-4">
            Try again, or refresh the page. If this keeps happening, contact support.
          </p>
          <button
            type="button"
            onClick={this.reset}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Try again
          </button>
          {import.meta.env.DEV && (
            <pre className="mt-6 text-[10px] text-left text-red-600 bg-red-50 border border-red-100 rounded-lg p-3 max-w-md overflow-auto">
              {this.state.error.name}: {this.state.error.message}
            </pre>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}
